/**
 * @file Main authentication controller for sign-in, auth state, and sign-out.
 */

import {
    OAuthProvider,
    getRedirectResult,
    onAuthStateChanged,
    signInWithPopup,
    signInWithRedirect,
    signOut,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-auth.js";
import { auth } from "../firebase/firebase.js";
import {
    activeListeners,
    setDataListenersAttachedStatus,
    state,
} from "../state/state.js";
import { setupAppEventListeners } from "../listeners/appEventListeners.js";
import { detachDetailViewListeners } from "../data/detailListeners.js";
import { renderHeader } from "../shell/appHeader.js";
import { detachRecallsRouteListeners } from "../../domains/recalls/recallsDataListeners.js";
import { detachCalendarRouteListeners } from "../../domains/calendar/calendarDataListeners.js";
import { detachAllOrderingListeners } from "../../domains/ordering/orderingListeners.js";
import { deactivateOrderingView } from "../../domains/ordering/orderingView.js";
import { resetOrderingRouteState } from "../../domains/ordering/orderingState.js";
import { deactivateComplianceView } from "../../domains/compliance/complianceView.js";
import {
    getItemType,
} from "../../shared/utils/itemLookup.js";
import { initializeAppWithUser } from "../data/appBootstrapListeners.js";
import { restorePostAuthDestination, registerAuthPopstateHandler } from "../routing/authRouteRestore.js";
import { renderAuthShell } from "./authShell.js";
import {
    capturePostAuthDestination,
    clearPostAuthDestination,
    isProtectedDestinationPath,
    readPostAuthDestination,
} from "./postAuthDestination.js";
import {
    clearSessionStartedAt,
    ensureSessionStartedAt,
    resetForcedMaintenanceSignOut,
} from "./sessionGuards.js";
import {
    detachUserInvitesListener,
    resetUserInvitesListenerState,
} from "../../domains/settings/userInviteListeners.js";

const LOGIN_NOTICE_KEY = "authNoticeMessage";
let appShellInitialized = false;
let popstateHandlerRegistered = false;
let redirectResolutionPromise = null;
let redirectFailureMessage = "";
let authChangeHandlerPromise = Promise.resolve();
let lastAppliedAuthStateKey = null;

const runtimeEnvConfig = window.__DFC_ENV_CONFIG__ || {};
const MICROSOFT_SCOPE_LIST = ["openid", "profile", "email", "User.Read"];
const isLocalAuthHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

function getMicrosoftProvider() {
    const provider = new OAuthProvider("microsoft.com");
    MICROSOFT_SCOPE_LIST.forEach((scope) => provider.addScope(scope));
    provider.setCustomParameters({
        tenant: runtimeEnvConfig?.auth?.microsoftTenant,
        prompt: runtimeEnvConfig?.auth?.microsoftPrompt,
    });
    return provider;
}

function queueLoginNotice(message) {
    if (message) {sessionStorage.setItem(LOGIN_NOTICE_KEY, message);}
}

function consumeLoginNotice() {
    const message = sessionStorage.getItem(LOGIN_NOTICE_KEY)
        || sessionStorage.getItem("inactiveAccountMessage");
    sessionStorage.removeItem(LOGIN_NOTICE_KEY);
    sessionStorage.removeItem("inactiveAccountMessage");
    return message;
}

async function handleMicrosoftSignIn() {
    renderAuthShell("auth_bootstrap_loading");

    try {
        capturePostAuthDestination({
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
            itemType: state.selectedItemId ? state.selectedItemType || getItemType(state.selectedItemId) : null,
            subView: state.currentView === "drugs" ? state.drugsView : null,
        });

        if (isLocalAuthHost) {
            await signInWithPopup(auth, getMicrosoftProvider());
            return;
        }

        await signInWithRedirect(auth, getMicrosoftProvider());
    } catch (error) {
        console.error("Microsoft sign-in error:", error);
        renderAuthShell("sign_in_failed", {
            message: `Sign-in failed: ${error.code || error.message}. Please try again.`,
        });
    }
}

function bindSignInButton() {
    const signInButton = document.getElementById("microsoft-signin-btn");
    if (signInButton) {
        signInButton.onclick = null;
        signInButton.onclick = handleMicrosoftSignIn;
    } else {
        console.error("Microsoft sign-in button not found.");
    }
}

function getStoredDestinationPath() {
    const destination = readPostAuthDestination();
    return destination?.path || null;
}

function getCurrentPath() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function preserveSignedOutDestination() {
    const currentPathname = window.location.pathname;
    if (!isProtectedDestinationPath(currentPathname)) {
        return null;
    }

    capturePostAuthDestination({
        pathname: currentPathname,
        search: window.location.search,
        hash: window.location.hash,
    });
    return readPostAuthDestination();
}

function renderSignedOutShell(options = {}) {
    const { resetPath = true } = options;
    preserveSignedOutDestination();
    const currentPath = getCurrentPath();
    const nextPath = resetPath
        ? "/"
        : currentPath;
    history.replaceState({ view: "tasks" }, "", nextPath);
    const message = consumeLoginNotice();
    if (message) {
        const shellState = message.includes("inactive") ? "inactive_blocked" :
            message.includes("maintenance") ? "maintenance_blocked" :
                "sign_in_failed";
        renderAuthShell(shellState, { message });
    } else if (redirectFailureMessage) {
        renderAuthShell("sign_in_failed", { message: redirectFailureMessage });
        redirectFailureMessage = "";
    } else {
        renderAuthShell("signed_out");
    }

    bindSignInButton();
}

async function waitForAuthStateReady() {
    if (typeof auth.authStateReady === "function") {
        await auth.authStateReady();
    }
}

async function resolveRedirectResultOnce() {
    if (redirectResolutionPromise) {return redirectResolutionPromise;}
    redirectResolutionPromise = (async () => {
        try {
            const redirectResult = await getRedirectResult(auth);
            return {
                error: null,
                user: redirectResult?.user || null,
            };
        } catch (error) {
            console.error("Redirect auth error:", error);
            return {
                error,
                user: null,
                message: `Sign-in failed: ${error.code || error.message}. Please try again.`,
            };
        }
    })();
    return redirectResolutionPromise;
}

function showLogin() {
    renderSignedOutShell();
}

function showApp() {
    renderAuthShell("app_ready");
    if (!appShellInitialized) {
        setupAppEventListeners();
        appShellInitialized = true;
    }
    renderHeader();
}

async function applyResolvedAuthState(user, redirectOutcome = null) {
    const authStateKey = user?.uid ? `user:${user.uid}` : "signed_out";
    if (!redirectOutcome && authStateKey === lastAppliedAuthStateKey) {
        return;
    }
    lastAppliedAuthStateKey = authStateKey;

    renderAuthShell("auth_bootstrap_loading");

    if (redirectOutcome?.error) {
        redirectFailureMessage = redirectOutcome.message;
        if (user) {
            await signOut(auth);
            return;
        }
    }

    if (user) {
        sessionStorage.removeItem("isExplicitSignOut");
        resetForcedMaintenanceSignOut();
        ensureSessionStartedAt();
        state.currentUser = user;
        await initializeAppWithUser(user, {
            queueLoginNotice,
            showApp,
            renderSignedOutShell,
            restorePostAuthDestination,
        });
        return;
    }

    state.currentUser = null;
    state.currentUserProfile = null;
    resetForcedMaintenanceSignOut();
    detachUserInvitesListener();
    detachCalendarRouteListeners();
    detachAllOrderingListeners();
    deactivateOrderingView();
    resetOrderingRouteState();
    deactivateComplianceView();
    clearSessionStartedAt();
    const explicitSignOut = sessionStorage.getItem("isExplicitSignOut");
    const currentPathIsProtected = isProtectedDestinationPath(window.location.pathname);
    const shouldSuppressDestinationPreservation = explicitSignOut && !currentPathIsProtected;
    const pendingDestination = shouldSuppressDestinationPreservation
        ? null
        : preserveSignedOutDestination();
    if (explicitSignOut) {
        sessionStorage.removeItem("isExplicitSignOut");
    }
    renderSignedOutShell({
        resetPath: !redirectFailureMessage &&
            !pendingDestination &&
            !getStoredDestinationPath() &&
            !currentPathIsProtected,
    });
}

/**
 * Initializes the primary authentication state listener.
 */
export async function init() {
    renderAuthShell("auth_bootstrap_loading");
    bindSignInButton();
    if (!popstateHandlerRegistered) {
        registerAuthPopstateHandler();
        popstateHandlerRegistered = true;
    }
    const redirectOutcome = await resolveRedirectResultOnce();
    await waitForAuthStateReady();
    const bootstrapUser = redirectOutcome?.user || auth.currentUser;
    await applyResolvedAuthState(bootstrapUser, redirectOutcome);

    onAuthStateChanged(auth, (user) => {
        authChangeHandlerPromise = authChangeHandlerPromise.then(async () => {
            await applyResolvedAuthState(user);
        }).catch((error) => {
            console.error("Error applying auth state change:", error);
        });
    });
}

/**
 * Signs out the current user and clears live listener state.
 */
export function handleLogout() {
    sessionStorage.setItem("isExplicitSignOut", "true");
    sessionStorage.removeItem("lastMainView");
    clearPostAuthDestination();
    clearSessionStartedAt();
    activeListeners.forEach(unsubscribe => {
        try { unsubscribe(); } catch (e) { console.warn("Error unsubscribing listener:", e); }
    });
    activeListeners.length = 0;
    setDataListenersAttachedStatus(false);
    resetUserInvitesListenerState();

    detachDetailViewListeners();
    detachRecallsRouteListeners();
    detachAllOrderingListeners();
    deactivateOrderingView();
    resetOrderingRouteState();
    deactivateComplianceView();
    history.replaceState({ view: "tasks" }, "", "/");

    signOut(auth).then(() => {
        // onAuthStateChanged handles the signed-out UI state.
    }).catch(error => {
        console.error("Sign out error:", error);
        showLogin();
    });
}
